import { useEffect, useState } from "react";
import { Tabs, Tab, Button, Chip, Tooltip, CircularProgress, Typography } from "@mui/material";
import { tss } from "tss-react/mui";
import { Scrollbars } from "react-custom-scrollbars";
import { config } from "config";
import { FileIcon, Report, DataGrid } from "components";
import ApiUtils from "api/Utils";
import { updateEntityToCache } from "api/Cache";
import { useLocation } from "react-router-dom";
import { icons } from "constants/viewer";
import { FoldersActions } from "actions";
import { getFileIcon } from "actions/GlobalActions";
import * as Utils from "services/UtilsService";
import { rawEntities } from "constants/common";
import { Link, CloudDownload, Warning } from "@mui/icons-material";
import { ReactComponent as File } from "assets/images/file.svg";
import commonStyles, { folderColor, rowHeight, iconColor } from "styles/common";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-material.css";
import { useVersioning } from "constants/common";
import { Div } from 'themes/jumbo/@jumbo/shared';

const useStyles = tss.create(({ theme }) => ({
    root: {
        //backgroundColor: "#f7f7f7",
        width: "100%",
        height: "100%",
    },
    tabs: {
        minHeight: 200,
        width: "100%",
        height: "100%",
        paddingTop: 0,
        //backgroundColor:theme.palette.background.default,
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.divider,
        overflow: "hidden",
    },
    firstCol: {
        paddingLeft: 20,
        width: 270,
        minWidth: 290,
        maxWidth: "100%",
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        cursor: "pointer",
    },
    secondCol: {
        width: "100%",
        minWidth: 260,
        maxWidth: 290,
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        height: "100%",
    },
    typography: {
        width: "100%",
    },
    row: {
        height: rowHeight,
        display: "inline-flex",
        alignItems: "center",
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.divider,
    },
    icon: {
        width: 27,
        height: 27,
        color: theme.palette.text.primary,
    },
    iconBig: {
        width: 40,
        height: 40,
        color: theme.palette.text.primary,
    },
    table: {
        width: "100%",
        height: "100%",
    },
    roundedCornersWarning: {
        borderRadius: "25px",
        background: "#f7a9ad",
        padding: "20px",
        marginRight: "10px",
    },
}));

function getIcon(entity: any, classes: any, big: boolean = false) {
    var fileName = getFileIcon(entity);
    //console.log("fileName:", fileName);
    //console.log("entity:", entity);
    if (!fileName) {
        return <File className={big ? classes.iconBig : classes.icon} />;
    }
    const Icon = icons[fileName];
    return (
        <div className={classes.layerIcon}>
            <Icon className={big ? classes.iconBig : classes.icon} />
        </div>
    );
}

const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
};

const FileRenderer = (props: any) => {
    const { classes: common } = commonStyles();
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
            }}
        >
            <FileIcon icon="file" />
            <Typography className={common.mediumLabel}>{props.value}</Typography>
        </div>
    );
};
const UrlRenderer = (props: any) => {
    let fileUrl = props.value;
    return (
        <div
            style={{
                display: "inline-flex",
                justifyContent: "right",
                width: "100%",
            }}
        >
            <Button
                size="small"
                onClick={() => {
                    copyToClipboard(fileUrl);
                }}
            >
                <Link />
            </Button>
            <Button size="small" href={fileUrl}>
                <CloudDownload />
            </Button>
        </div>
    );
};

type IProps = {
    entity: any;
    maxWidth?: any;
    maxHeight?: any;
    isReady?: boolean;
};

function TabShowProcessLog(props: { isLogReady?: boolean; logReport: string; maxHeight: number; errorInfo: string }) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { isLogReady, logReport, maxHeight } = props;
    let { errorInfo } = props;

    try {
        errorInfo = atob(errorInfo);
    } catch (ex) {
        errorInfo = "";
    }

    // Sometimes, the first character of the Error message is a newline. This breaks the visual aspect of the error message in the UI.
    if (errorInfo.substring(0, 1) === "\n") {
        errorInfo = errorInfo.substring(1);
    }

    return (
        <div role="tabpanel" className={classes.tabs}>
            <Scrollbars
                autoHeight
                autoHeightMax={maxHeight}
                style={{ overflowX: "hidden", zIndex: 1000, userSelect: "text" }}
            >
                <div style={{ padding: "20px" }}>
                    {errorInfo ? (
                        <div style={{ paddingBottom: "20px" }}>
                            <div
                                style={{ display: "flex", alignItems: "center" }}
                                className={classes.roundedCornersWarning}
                            >
                                <Warning fontSize="large" />
                                <Typography
                                    style={{ paddingLeft: "20px", whiteSpace: "pre-line" }}
                                    className={cx(common.mediumLabel, classes.typography)}
                                >
                                    {errorInfo}
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        ""
                    )}
                    {isLogReady ? (
                        <Typography
                            style={{ whiteSpace: "pre-line" }}
                            className={cx(common.mediumLabel, classes.typography)}
                        >
                            {!logReport ? "No log available" : logReport}
                        </Typography>
                    ) : (
                        <div
                            style={{ width: "100%", height: "80%", paddingTop: "40px" }}
                            className={common.middleCenter}
                        >
                            <CircularProgress />
                        </div>
                    )}
                </div>
            </Scrollbars>
        </div>
    );
}

function TabPanel(props: {
    entity: any;
    properties: any;
    isFiles?: boolean;
    maxHeight?: any;
    isDownloading?: boolean;
    isReady?: boolean;
}) {
    const { classes, cx } = useStyles();
    const { entity, properties, isFiles, maxHeight, isDownloading, isReady } = props;
    const { classes: common } = commonStyles();
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);

    useEffect(() => {
        const getData = () => {
            let curCols: any = [
                { field: "fileName", cellRenderer: FileRenderer, width: 400, editable: false },
                { field: "URL", headerName: "", cellRenderer: UrlRenderer },
            ];
            let curRows: any = [];
            let data: any = [];
            properties.forEach((name: string) => {
                if (isFiles) {
                    let fileUrl = ApiUtils.getFileURL(
                        entity["project"],
                        entity["ID"],
                        name,
                        "application/octet-stream"
                    );
                    curRows.push({ fileName: name, URL: fileUrl });
                } else {
                    let value =
                        name === "file_date" || name === "processing_start_date" || name === "capture_date"
                            ? Utils.formatDateWithTime(entity[name])
                            : entity[name];
                    data[name] = value;
                }
            });
            if (isFiles) {
                //console.log("curCols:", curCols);
                //console.log("curRows:", curRows);
                setColumns(curCols);
                setRows(curRows);
            } else {
                setData(data);
            }
        };
        getData();
    }, [isFiles, properties, entity]);

    return (
        <div role="tabpanel" className={classes.tabs}>
            {isDownloading || !isReady ? (
                <div style={{ width: "100%", height: "80%" }} className={common.middleCenter}>
                    <CircularProgress />
                </div>
            ) : properties.length > 0 ? (
                isFiles ? (
                    <div className={"ag-theme-material"} style={{ width: "100%", height: "100%" }}>
                        <DataGrid rows={rows} columns={columns} maxWidth={"100%"} maxHeight={"100%"} />
                    </div>
                ) : (
                    <Scrollbars
                        autoHeight
                        autoHeightMax={maxHeight}
                        style={{ overflowX: "hidden", zIndex: 1000, userSelect: "text" }}
                    >
                        <table className={classes.table} cellPadding={10} cellSpacing={0}>
                            <tbody>
                                {Object.keys(data).map((name: string, index: number) => (
                                    <tr key={"prop-" + index}>
                                        <td className={cx(common.middle, classes.firstCol, classes.row)}>
                                            <Tooltip title={name}>
                                                <Typography
                                                    className={cx(common.mediumLabel, classes.typography)}
                                                    noWrap
                                                    sx={{color: (theme) => theme.palette.text.primary}}
                                                >
                                                    {name}
                                                </Typography>
                                            </Tooltip>
                                        </td>
                                        <td className={cx(common.middle, classes.secondCol, classes.row)}>
                                            <Typography className={cx(common.mediumLabel, classes.typography)} noWrap>
                                                {name === "file_date" ||
                                                name === "processing_start_date" ||
                                                name === "capture_date"
                                                    ? Utils.formatDateWithTime(entity[name])
                                                    : entity[name]}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Scrollbars>
                )
            ) : (
                <table className={classes.table}>
                    <tbody>
                        <tr>
                            <td
                                className={cx(common.middle)}
                                style={{
                                    paddingLeft: 20,
                                    paddingTop: 10,
                                }}
                            >
                                <Typography sx={{ fontSize: 14 }}>There are no properties in this section</Typography>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
}

function handleTabs(
    tab: number,
    entity: any,
    filesList: any,
    maxWidth: any,
    maxHeight: any,
    isDownloading: boolean,
    isLogReady: boolean,
    logReport: string,
    isReady: any
) {
    switch (tab) {
        case 0: //General
            const generalProperties = Object.getOwnPropertyNames(entity).filter((name: any) => {
                return name !== "thumb" && !name.includes("input_") && !name.includes("coord_");
            });
            return <TabPanel isReady={isReady} entity={entity} properties={generalProperties} maxHeight={maxHeight} />;
        case 1: //Inputs
            const inputProperties = Object.getOwnPropertyNames(entity).filter((entry: any) => {
                return entry.includes("input_");
            });
            return <TabPanel isReady={isReady} entity={entity} properties={inputProperties} maxHeight={maxHeight} />;
        case 2: //Coordinates
            const coordProperties = Object.getOwnPropertyNames(entity).filter((entry: any) => {
                return entry.includes("coord_");
            });
            return <TabPanel isReady={isReady} entity={entity} properties={coordProperties} maxHeight={maxHeight} />;
        case 3: //Files
            return (
                <TabPanel
                    entity={entity}
                    properties={filesList}
                    isFiles={true}
                    isReady={isReady}
                    maxHeight={maxHeight}
                    isDownloading={isDownloading}
                />
            );
        case 4: //Results
            return <Report entity={entity} maxHeight={maxHeight} maxWidth={maxWidth} />;
        case 5: //Process Log
            return (
                <TabShowProcessLog
                    isLogReady={isLogReady}
                    logReport={logReport}
                    maxHeight={maxHeight}
                    errorInfo={entity.error_info}
                />
            );
        default:
            return <div>{tab}</div>;
    }
}

function EntityHeader(props: IProps) {
    const { entity } = props;
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const workflow = FoldersActions.getWorkflow(entity);
    return (
        <Div className={common.middle} style={{ margin: 20 }} sx={{flexDirection: {xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row'}}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
                {getIcon(entity, classes, true)}
                <Typography style={{ marginLeft: 10, fontSize: 20 }}>{entity.name}</Typography>
            </div>
            <div>
                {entity["state"] === "COMPLETE" ? (
                    <Chip 
                        label="Complete"   
                        size="small" 
                        sx={{ marginLeft: 20, backgroundColor: (theme) => theme.palette.success.dark, color: (theme) => theme.palette.text.primary  }} 
                    />
                ) : entity["state"] === "ERROR" ? (
                    <Chip label="Failed" size="small" sx={{ marginLeft: 20, backgroundColor: "#F08B7B" }} />
                ) : (
                    <Chip label="Pending" size="small" sx={{ marginLeft: 20, backgroundColor: "#f4ea95" }} />
                )}
                { useVersioning && (
                    <>
                        {workflow === null ? (
                            <Chip label="Not Versioned" size="small" style={{ marginLeft: 7 }} />
                        ) : (
                            <div>
                                <Chip
                                    label={"Version: " + workflow["version"]}
                                    size="small"
                                    style={{ marginLeft: 7, backgroundColor: folderColor }}
                                />
                                <Chip label={workflow["products"].join(" - ")} size="small" style={{ marginLeft: 7 }} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </Div>
    );
}
export default function EntityProperties(props: IProps) {
    const { classes } = useStyles();
    const { entity, maxWidth, maxHeight } = props;
    //console.log("entityProperties.entity:", entity);
    const file_type = entity["file_type"];
    const isReport = file_type === "REPORT";
    const isRaw = rawEntities.includes(file_type);
    const { hash } = useLocation();

    const [tab, setTab] = useState(0);
    const [filesList, setFilesList] = useState<any>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isLogReady, setIsLogReady] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [curEntity, setcurEntity] = useState(entity);
    const [logReport, setLogReport] = useState("");
    const [entityID, setEntityID] = useState("");

    useEffect(() => {
        if (entity && entity !== null && isRaw) {
            const isReport = entity["file_type"] === "REPORT";
            if (isReport) {
                setTab(4);
            }
            setIsDownloading(true);
            ApiUtils.getFilesList(entity.project, entity.ID, (list: any) => {
                var arList = list.split("\n");
                arList = arList.filter((file: string) => {
                    return file !== "";
                });
                setFilesList(arList);
                setIsDownloading(false);
            });
        }
    }, [entity, isRaw]);

    useEffect(() => {
        if (hash === "#processlog") {
            setTab(5);
        }
    }, [hash]);

    useEffect(() => {
        const logFilename = "processor.log";
        setIsLogReady(false);
        if (entityID !== entity["ID"]) {
            setEntityID(entity["ID"]);
            setLogReport("Downloading log...");
            let fileUrl = ApiUtils.getFileURL(entity["project"], entity["ID"], logFilename, "application/octet-stream");
            ApiUtils.getFileContent(fileUrl).then((logText: any) => {
                console.log("PROCESS LOG : " + logText)
                setLogReport(logText);
                setIsLogReady(true);
            });
        } else if (!isDownloading) {
            setIsLogReady(true);
        }
    }, [entity, isDownloading, logReport, entityID]);

    useEffect(() => {
        if (Boolean(config.api.filters === "true")) {
            ApiUtils.getEntity(entity.ID, entity.project, (entity: any) => {
                console.log("entity:", entity);
                updateEntityToCache("entities", entity.project, entity);
                if (
                    (curEntity["entity"] &&
                        curEntity["entity"] !== null &&
                        Object.getOwnPropertyNames(curEntity["entity"]).length !==
                            Object.getOwnPropertyNames(entity).length) ||
                    (curEntity["file_type"] &&
                        Object.getOwnPropertyNames(curEntity).length !== Object.getOwnPropertyNames(entity).length)
                ) {
                    if (curEntity["file_type"]) {
                        setcurEntity(entity);
                        setIsReady(true);
                    } else {
                        const newentity = { ...curEntity };
                        newentity["entity"] = entity;
                        setcurEntity(newentity);
                        setIsReady(true);
                    }
                    console.log("update curEntity with entity:", entity);
                } else {
                    setIsReady(true);
                }
            });
        } else {
            setIsReady(true);
        }
    }, [entity, curEntity]);

    if (!entity || entity == null) {
        return <div>wrong Entity</div>;
    }

    const handleTabChange = (e: any, newTab: number) => {
        setTab(newTab);
    };

    return (
        <div className={classes.root}>
            <EntityHeader entity={entity} />
            <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary" style={{ marginTop: 0 }} variant="scrollable">
                {isReport ? <Tab label="Results" value={4} /> : ""}
                <Tab label="General" value={0} />
                <Tab label="Inputs" value={1} />
                <Tab label="Coordinates" value={2} />
                {isRaw ? <Tab label="Files" value={3} /> : ""}
                <Tab label="Process Log" value={5} />
            </Tabs>
            {handleTabs(tab, curEntity, filesList, maxWidth, maxHeight, isDownloading, isLogReady, logReport, isReady)}
        </div>
    );
}
